/** Normalize theme keys: B / a / t accent themes; r or empty = default (no attribute). */
export function normalizeBratoTheme(value: string | null | undefined): string | null {
  if (!value || value === 'l' || value === 'r') return null;
  if (value === 'V' || value === 'B') return 'B';
  if (value === 'k' || value === 't') return 't';
  if (value === 'a') return 'a';
  return null;
}

/**
 * Read theme synchronously at boot — BEFORE the React tree mounts and BEFORE
 * the settings store hydrates. This function MUST stay synchronous (it runs
 * from main.tsx pre-render to avoid a flash of wrong theme on cold boot).
 *
 * settings-zustand-migration Phase 2: reads in priority order:
 *   (1) the new persist blob `va-settings-v1` (forward source)
 *   (2) legacy `brato-theme` (write-through mirror from store.setTheme)
 *   (3) legacy `valko-theme` (pre-rebrand fallback)
 *
 * `setTheme` in settings.store.ts mirrors writes to `brato-theme` so this fast
 * path stays warm — no need to JSON.parse the blob on the hot path unless the
 * mirror was somehow cleared.
 */
function readFromPersistBlob(): string | null {
  try {
    const raw = localStorage.getItem('va-settings-v1');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { theme?: string | null } };
    return parsed?.state?.theme ?? null;
  } catch {
    return null;
  }
}

/** Read theme from storage (supports legacy `valko-theme`). */
export function readInitialBratoTheme(): string | null {
  try {
    const fromStore = readFromPersistBlob();
    const merged = fromStore ?? localStorage.getItem('brato-theme') ?? localStorage.getItem('valko-theme');
    return normalizeBratoTheme(merged);
  } catch {
    return null;
  }
}

/** Apply saved Brato theme before any route renders. Migrates `valko-theme` → `brato-theme`. */
export function syncBratoThemeFromStorage(): void {
  try {
    const fromStore = readFromPersistBlob();
    const v = localStorage.getItem('valko-theme');
    const b = localStorage.getItem('brato-theme');
    const merged = fromStore ?? b ?? v;
    const t = normalizeBratoTheme(merged);
    localStorage.removeItem('valko-theme');
    if (t) localStorage.setItem('brato-theme', t);
    else localStorage.removeItem('brato-theme');
    if (t) document.documentElement.setAttribute('data-theme', t);
    else document.documentElement.removeAttribute('data-theme');
  } catch {
    /* ignore */
  }
}
